import { ChatLogger, chatLoggerFactory } from '@/components/chat/ChatLogger'
import { ChatMessageSentEmailNotificationHandler } from '@/components/chat/ChatStore/ChatEmailNotificationHandler'
import { ConversationType, GroupChannelType } from '@/interfaces/chat'
import { IUser } from '@/interfaces/user'
import { createChannel, deleteDmChannel } from '@/services/chat'
import { environment } from '@/shared/consts/environment'
import { DEFAULT_AVATAR } from '@/shared/consts/imagesPaths'
import { userFullName } from '@/shared/utils'
import SendbirdChat, {
  ConnectionHandler,
  UserEventHandler,
} from '@sendbird/chat'
import {
  GroupChannel,
  GroupChannelHandler,
  GroupChannelModule,
  GroupChannelUpdateParams,
} from '@sendbird/chat/groupChannel'
import { userSendBirdMetaData } from '../Chat.utils'
import { ChatChannelsCollection } from './ChatChannelsCollection'
import { ChatStatusType, ChatStoreState } from './ChatStoreContext'

export class ChatStore {
  private logger: ChatLogger
  private subscribers = new Set<() => void>()
  private sendbirdSdk: SendbirdChat & { groupChannel: GroupChannelModule }
  private creatorlandUser: IUser
  private readonly handlerIdPrefix = new Date().toISOString()
  public messageSentEmailNotificationHandlers: Record<
    string,
    ChatMessageSentEmailNotificationHandler
  > = {}
  // public onlineUsers: any
  public connectionChannelsCollection: ChatChannelsCollection
  public connectionRequestChannelsCollection: ChatChannelsCollection
  public dealChannelsCollection: ChatChannelsCollection
  public channelEventHandlers: GroupChannelHandler[] = []
  private userEventHandlers: UserEventHandler[] = []

  state: Omit<ChatStoreState, 'hasUnreadMessages'> = {
    connectionRequestChannelsUnreadMessagesCount: 0,
    connectionChannelsUnreadMessagesCount: 0,
    dealChannelsUnreadMessagesCount: 0,
    activeChannel: null,
    status: ChatStatusType.NOT_INITIALAZED,
  }

  constructor() {
    this.logger = chatLoggerFactory.createLogger('ChatStore')
    this.sendbirdSdk = SendbirdChat.init({
      appId: environment.sendbirdAppId,
      modules: [new GroupChannelModule()],
    }) as unknown as SendbirdChat & { groupChannel: GroupChannelModule }

    this.connectionChannelsCollection = new ChatChannelsCollection(
      this.sendbirdSdk,
      GroupChannelType.connection
    )
    this.connectionRequestChannelsCollection = new ChatChannelsCollection(
      this.sendbirdSdk,
      GroupChannelType.connectionRequest
    )
    this.dealChannelsCollection = new ChatChannelsCollection(
      this.sendbirdSdk,
      GroupChannelType.deal
    )
  }

  init = async (user: IUser) => {
    if (
      !user ||
      this.state.status === ChatStatusType.INITIALIZED ||
      this.state.status === ChatStatusType.INITIALIZING
    )
      return
    this.creatorlandUser = user
    this.updateState('status', 'initializing')
    try {
      this.logger.debug('initializing sdk')
      await this.sendbirdSdk.connect(user.id)
      await Promise.all([
        await this.syncUser(),
        await this.syncUnreadMessageCount(),
        await this.connectionRequestChannelsCollection.listChannels(),
        await this.connectionChannelsCollection.listChannels(),
        await this.dealChannelsCollection.listChannels(),
      ])

      this.addEventListeners()
      this.updateState('status', 'initialized')
      this.logger.debug('initialization completed')
    } catch (e) {
      this.updateState('status', 'failed')
      this.logger.error('Unable to initialize chat store', e)
    }
  }

  subscribe = (subscriber) => {
    this.subscribers.add(subscriber)
    return () => {
      this.subscribers.delete(subscriber)
    }
  }

  getSnapshot = () => {
    return this.state
  }

  notifySubscribers = () => {
    this.subscribers.forEach((subscriber) => subscriber())
  }

  registerEventHandlers = (
    handlers: GroupChannelHandler | UserEventHandler
  ) => {
    if (handlers instanceof GroupChannelHandler) {
      this.channelEventHandlers.push(handlers)
    } else if (handlers instanceof UserEventHandler) {
      this.userEventHandlers.push(handlers)
    }
  }

  removeEventHandlers = (handlers: GroupChannelHandler | UserEventHandler) => {
    if (handlers instanceof GroupChannelHandler) {
      this.channelEventHandlers = this.channelEventHandlers.filter(
        (h) => h !== handlers
      )
    } else if (handlers instanceof UserEventHandler) {
      this.userEventHandlers = this.userEventHandlers.filter(
        (h) => h !== handlers
      )
    }
  }

  searchChannels = async (searchTerm: string) => {
    await Promise.all([
      this.connectionChannelsCollection.listChannels(searchTerm),
      this.connectionRequestChannelsCollection.listChannels(searchTerm),
    ])
  }

  createChannel = async (invitedUserIds: string[]) => {
    this.logger.debug('creating channel', invitedUserIds)
    const res = await createChannel(invitedUserIds)
    if ('error' in res) return res
    try {
      const newChannel = await this.sendbirdSdk.groupChannel.getChannel(
        res.channelUrl
      )
      this.connectionChannelsCollection.addChannel(newChannel)
      if (!this.messageSentEmailNotificationHandlers[newChannel.url]) {
        this.messageSentEmailNotificationHandlers[newChannel.url] =
          new ChatMessageSentEmailNotificationHandler(
            newChannel,
            this.sendbirdSdk.currentUser.userId
          )
      }
      return newChannel
    } catch (e) {
      this.logger.error('Error while creating channel', e)
      return { error: e }
    }
  }

  sendMessage = async (message: string | File) => {
    this.logger.debug('sending message')
    if (!this.state.activeChannel) return
    try {
      const activeChannel = this.state.activeChannel
      await activeChannel.endTyping()

      if (typeof message === 'string') {
        await this.sendTextMessage(activeChannel, message)
      } else {
        await this.sendFileMessage(activeChannel, message)
      }
      if (this.messageSentEmailNotificationHandlers[activeChannel.url]) {
        this.messageSentEmailNotificationHandlers[
          activeChannel.url
        ].onSendMessage()
      }
      return activeChannel
    } catch (e) {
      this.logger.error('Error while sending message', e)
      return { error: e }
    }
  }

  leaveChannel = async (channel: GroupChannel) => {
    this.logger.debug('leaving channel', channel.url)
    try {
      const metadata = await channel.getMetaData(['conversationType'])
      if (
        (metadata.conversationType &&
          metadata.conversationType === ConversationType.dm) ||
        (!metadata.conversationType && channel.memberCount === 2)
      ) {
        await deleteDmChannel(channel.url)
      } else {
        await channel.leave()
      }
      return true
    } catch (e) {
      this.logger.error('Error while leaving channel', e)
      return { error: e }
    }
  }

  changeChannelCustomType = async (
    channel: GroupChannel,
    customType: GroupChannelType
  ) => {
    this.logger.debug('changing channel custom type', channel)
    try {
      const params: GroupChannelUpdateParams = {
        customType: customType,
      }
      await channel.updateChannel(params)
      return
    } catch (e) {
      this.logger.error('Error while updating channel', e)
      return { error: e }
    }
  }

  selectChannel = (channel: GroupChannel | null) => {
    this.logger.debug('selected channel', channel?.url)
    this.updateState('activeChannel', channel)
    if (!!channel && !this.messageSentEmailNotificationHandlers[channel.url]) {
      this.messageSentEmailNotificationHandlers[channel.url] =
        new ChatMessageSentEmailNotificationHandler(
          channel,
          this.sendbirdSdk.currentUser.userId
        )
    }
  }

  markAsRead = async (channel?: GroupChannel): Promise<void> => {
    if (channel) return await channel.markAsRead()
    if (
      !this.state.activeChannel ||
      !this.state.activeChannel?.lastMessage ||
      this.state.activeChannel.unreadMessageCount === 0
    )
      return
    try {
      if (this.state.activeChannel.unreadMessageCount > 0) {
        this.logger.debug('marking channel as read')
        return await this.state.activeChannel.markAsRead()
      }
      return
    } catch (e) {
      this.logger.error('Unable to mark as read', e)
    }
  }

  startTyping = async () => {
    if (!this.state.activeChannel) return
    this.logger.debug('calling start typing')
    try {
      await this.state.activeChannel.startTyping()
    } catch (e) {
      this.logger.error('Start typing error', e)
    }
  }

  endTyping = async () => {
    if (!this.state.activeChannel) return
    this.logger.debug('calling start typing')
    try {
      await this.state.activeChannel.endTyping()
    } catch (e) {
      this.logger.error('End typing error', e)
    }
  }

  private sendTextMessage = (channel: GroupChannel, message: string) => {
    return new Promise((resolve, reject) => {
      channel.sendUserMessage({ message }).onSucceeded(resolve).onFailed(reject)
    })
  }

  private sendFileMessage = (channel: GroupChannel, message: File) => {
    return new Promise((resolve, reject) => {
      channel
        .sendFileMessage({ file: message })
        .onSucceeded(resolve)
        .onFailed(reject)
    })
  }

  private addEventListeners = () => {
    const groupChannelHandler = new GroupChannelHandler({
      onChannelChanged: this.onChannelChanged,
      onTypingStatusUpdated: this.onTypingStatusUpdated,
      onChannelDeleted: this.onChannelDeleted,
      onUnreadMemberStatusUpdated: this.onUnreadMemberStatusUpdated,
    })
    const userEventHandler = new UserEventHandler({
      onTotalUnreadMessageCountUpdated: this.onTotalUnreadMessageCountUpdated,
    })
    const connectionEventHandler = new ConnectionHandler({
      onReconnectFailed: this.onReconnectFailed,
      onDisconnected: this.onDisconnected,
      onReconnectStarted: this.onReconnectStarted,
      onReconnectSucceeded: this.onReconnectSucceeded,
    })
    this.sendbirdSdk.groupChannel.addGroupChannelHandler(
      `${this.handlerIdPrefix}-group-channel-${this.sendbirdSdk.currentUser.userId}`,
      groupChannelHandler
    )
    this.sendbirdSdk.addUserEventHandler(
      `${this.handlerIdPrefix}-user-${this.sendbirdSdk.currentUser.userId}`,
      userEventHandler
    )
    this.sendbirdSdk.addConnectionHandler(
      `${this.handlerIdPrefix}-connection-${this.sendbirdSdk.currentUser.userId}`,
      connectionEventHandler
    )
  }

  private removeEventListeners = () => {
    this.sendbirdSdk.groupChannel.removeGroupChannelHandler(
      `${this.handlerIdPrefix}-group-channel-${this.sendbirdSdk.currentUser.userId}`
    )
    this.sendbirdSdk.removeUserEventHandler(
      `${this.handlerIdPrefix}-user-${this.sendbirdSdk.currentUser.userId}`
    )
  }

  private onDisconnected = () => {
    this.logger.debug('onDisconnected called')
  }
  private onReconnectStarted = () => {
    this.logger.debug('onReconnectStarted called')
  }
  private onReconnectSucceeded = () => {
    this.logger.debug('onReconnectSucceeded called')
  }
  private onReconnectFailed = () => {
    this.logger.debug('onReconnectFailed called')
    this.sendbirdSdk.reconnect()
  }

  private syncUser = async () => {
    this.logger.debug('syncing current user')
    const fullName = userFullName(this.creatorlandUser)
    const userMetaData = userSendBirdMetaData(this.creatorlandUser)
    await this.sendbirdSdk.updateCurrentUserInfo({
      nickname: fullName,
      profileUrl: this.creatorlandUser?.avatar
        ? this.creatorlandUser.avatar
        : DEFAULT_AVATAR,
    })
    await this.sendbirdSdk.currentUser.updateMetaData(userMetaData, true)
  }

  private onChannelChanged = async (channel) => {
    this.logger.info('onChannelChanged called', channel.url)
    if (this.state.activeChannel?.url === channel?.url) {
      this.updateState('activeChannel', channel)
    }
    if (this.messageSentEmailNotificationHandlers[channel.url]) {
      this.messageSentEmailNotificationHandlers[channel.url].onChannelChanged(
        channel
      )
    }
  }

  private onUnreadMemberStatusUpdated = async (channel: GroupChannel) => {
    this.logger.info('onUnreadMemberStatusUpdated called', channel.url)
    if (this.state.activeChannel?.url === channel.url) {
      this.updateState('activeChannel', channel)
    }
    if (this.messageSentEmailNotificationHandlers[channel.url]) {
      this.messageSentEmailNotificationHandlers[
        channel.url
      ].onUnreadMemberStatusUpdated(channel)
    }
  }

  private onChannelDeleted = async (channel) => {
    this.logger.info('onChannelDeleted called', channel)
  }

  private onTotalUnreadMessageCountUpdated = async (
    totalCount,
    countByCustomTypes
  ) => {
    const connectionChannelsCount = countByCustomTypes['connection']
    const connectionRequestChannelsCount =
      countByCustomTypes['connection-request']
    const dealChannelsCount = countByCustomTypes['deal']
    this.logger.info(
      'onTotalUnreadMessageCountUpdated called',
      totalCount,
      countByCustomTypes
    )
    this.updateState(
      'connectionChannelsUnreadMessagesCount',
      connectionChannelsCount
    )
    this.updateState(
      'connectionRequestChannelsUnreadMessagesCount',
      connectionRequestChannelsCount
    )
    this.updateState('dealChannelsUnreadMessagesCount', dealChannelsCount)
  }

  private onTypingStatusUpdated = async (channel: GroupChannel) => {
    this.logger.info('onTypingStatusUpdated called', channel.url)
    for (const handlers of this.channelEventHandlers) {
      if (handlers.onTypingStatusUpdated) {
        handlers.onTypingStatusUpdated(channel)
      }
    }
  }

  private updateState = (
    propName: keyof ChatStoreState,
    propValue: unknown
  ) => {
    this.state = { ...this.state, [propName]: propValue }
    this.notifySubscribers()
  }

  private syncUnreadMessageCount = async () => {
    this.logger.debug('syncing unread message count')
    try {
      const [
        connectionChannelsCount,
        connectionRequestChannelsCount,
        dealChannelsCount,
      ] = await Promise.all([
        this.sendbirdSdk.groupChannel.getTotalUnreadMessageCount({
          channelCustomTypesFilter: ['connection'],
        }),
        this.sendbirdSdk.groupChannel.getTotalUnreadMessageCount({
          channelCustomTypesFilter: ['connection-request'],
        }),
        this.sendbirdSdk.groupChannel.getTotalUnreadMessageCount({
          channelCustomTypesFilter: ['deal'],
        }),
      ])
      this.updateState(
        'connectionChannelsUnreadMessagesCount',
        connectionChannelsCount
      )
      this.updateState(
        'connectionRequestChannelsUnreadMessagesCount',
        connectionRequestChannelsCount
      )
      this.updateState('dealChannelsUnreadMessagesCount', dealChannelsCount)
    } catch (e) {
      this.logger.error('onChannelChanged error', e)
    }
  }
}

export const chatStore = new ChatStore()
global.chatStore = chatStore
